import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { api } from "@/api/api";
import {
  PurchaseStatus,
  type PaymentMethod,
  type PaymentResult,
} from "@/app/payments/types";
import { platformApi } from "@/app/platform";
import { useCheckoutPayment } from "@/hooks/useCheckoutPayment";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { TemporallyStoreFlightBookingDetailsBodyInputDto } from "@/types/codegen";
import { updatePersonalData } from "@/utils/bookings";

type PaymentParams = TemporallyStoreFlightBookingDetailsBodyInputDto & {
  offerId: string;
  promoCode?: string;
};

type BnplPaymentParams = PaymentParams & {
  sessionId: string;
  otp: string;
};

type Props = {
  onSuccess: (result: PaymentResult) => void;
  initialAvailabilityToken?: string;
  method?: PaymentMethod;
};

export const useFlightBooking = ({
  onSuccess,
  initialAvailabilityToken,
  method,
}: Props) => {
  const [checkoutPurchasePending, setCheckoutPurchasePending] = useState(false);

  const { flowComponent, isLoadingForm } = useCheckoutPayment({
    availabilityToken: initialAvailabilityToken,
    serviceType: "air",
    enabled: method === "checkout",
    onSuccess,
    onSubmit: () => setCheckoutPurchasePending(true),
  });

  const handleError = useCallback((error: unknown) => {
    console.error("Error in booking or payment process:", error);
    Sentry.captureException(error);
  }, []);

  const checkoutPayment = {
    // TODO: promoCode is not being passed to order creation, checkout payment must be refactored before it's possible
    submit: async ({ offerId, promoCode, ...postData }: PaymentParams) => {
      try {
        if (!flowComponent) {
          return;
        }

        const wasSuccess = await updatePersonalData();
        if (!wasSuccess) {
          return {
            orderId: "",
            status: PurchaseStatus.CANCELLED,
          };
        }

        await api.post(
          `/flights/${offerId}/book`,
          postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
        );
        flowComponent.submit();
      } catch (error) {
        handleError(error);
        setCheckoutPurchasePending(false);
      }
    },
    isLoadingCheckoutForm: isLoadingForm,
    isPending: checkoutPurchasePending,
  };

  const qiPayment = useMutation({
    mutationFn: async ({ offerId, promoCode, ...postData }: PaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(
        `/flights/${offerId}/book`,
        postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
      );

      // Use standard payment flow
      return platformApi.getPaymentProvider("standard").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        serviceType: "air",
        promoCode,
        onSuccess: ({ orderId }) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_SUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "Success",
            transaction_id: orderId,
          });
        },
        onFailure: ({ orderId }) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "fail",
            transaction_id: orderId,
          });
        },
      });
    },
    onSuccess,
    onError: handleError,
  });

  const bnplPayment = useMutation({
    mutationFn: async ({
      offerId,
      sessionId,
      otp,
      promoCode,
      ...postData
    }: BnplPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(
        `/flights/${offerId}/book`,
        postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
      );

      return platformApi.getPaymentProvider("bnpl").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        sessionId,
        otp,
        promoCode,
      });
    },
    onSuccess,
    onError: handleError,
  });

  return {
    qiPayment,
    checkoutPayment,
    bnplPayment,
  };
};
